/**
 * The "Break-My-App" edge-case fuzzer.
 *
 * Given an inferred schema it synthesizes rows that are *schema plausible*
 * (right keys, right shapes) but maximally hostile to importers, ORMs and APIs.
 * The classic payload libraries live here:
 *
 *   SQL injection   →  ' OR 1=1 --, '); DROP TABLE users; --
 *   XSS             →  <script>alert(1)</script>, <img src=x onerror=…>
 *   Unicode torture →  👨‍👩‍👧‍👦 (ZWJ family), RTL Arabic/Hebrew, full-width, combining marks
 *   null-likes      →  "null", "undefined", "NaN", "nil", "\N"
 *   boundary strings→  exactly 255 / 256 chars, embedded newlines & tabs
 *   numeric traps   →  0, -0, -999999.99, 0.30000000000000004 (0.1 + 0.2),
 *                     MAX_SAFE_INTEGER, 1e21, 5e-324, "1,234.56"
 *   date traps      →  2024-02-29 leap day, 1970-01-01T00:00:00Z epoch zero,
 *                     2038-01-19 32-bit overflow, 9999-12-31, invalid 2023-02-29
 *
 * Generation is seeded (mulberry32) so the same seed always reproduces the same
 * dataset — a failing row number is reproducible in a bug report.
 */
import type { ChaosRow, ColumnSchema } from './types';

/* ----------------------------- payload pools ----------------------------- */

const SQL_INJECTION = [
  "' OR 1=1 --",
  "'; DROP TABLE users; --",
  "1' OR '1'='1",
  "admin'--",
  '" OR ""="',
  "'; WAITFOR DELAY '0:0:5'--",
  "1; UPDATE users SET role='admin'",
  "' UNION SELECT NULL, version() --",
];

const XSS = [
  '<script>alert(1)</script>',
  '<img src=x onerror=alert(1)>',
  '<svg/onload=alert(1)>',
  '"><script>alert(String.fromCharCode(88,83,83))</script>',
  '<iframe src="javascript:alert(1)"></iframe>',
  '<a href="javascript:alert(document.domain)">click</a>',
];

const UNICODE_TORTURE = [
  '👨‍👩‍👧‍👦',            // ZWJ family sequence
  '🧑🏾‍🚀',                // ZWJ + skin tone modifier
  '🇺🇸🏳️‍🌈',              // flag + ZWJ + rainbow flag
  'مرحبا بالعالم',          // Arabic (RTL)
  'אבג דהו וזחט',          // Hebrew (RTL)
  'こんにちは世界',          // Japanese
  '🇨🇳 测试数据',            // Chinese + regional indicator
  'e\u0301galite\u0301',     // combining accents
  '𝕳𝖊𝖑𝖑𝖔 𝖜𝖔𝖗𝖑𝖉',          // mathematical alphanumerics
  'Ω≈ç√∫˜µ≤≥÷',             // symbols & math
  'ﬁ ﬂ ﬀ ligature',  // typographic ligatures
];

const NULL_LIKE_STRINGS = [
  'null',
  'NULL',
  'Null',
  'undefined',
  'NaN',
  'nil',
  'None',
  '(null)',
  '\\N',
  '-',
];

const WHITESPACE_TRAPS = [
  '   padded   ',
  '\ttab-indented',
  'line\nbreak',
  'trailing space ',
  ' leading space',
  '\u00A0nbsp\u00A0',
  'zero\u200Bwidth',
  'carriage\rreturn',
];

const REST = [
  'O\'Brien',
  'comma,inside',
  'quote"inside',
  '<>&<>',
  '💥',
  'percent%sign',
  'back\\slash',
  'a'.repeat(255),
  'a'.repeat(256),
  '🚀'.repeat(64),
];

const STRING_POOL: string[] = [
  ...SQL_INJECTION,
  ...XSS,
  ...UNICODE_TORTURE,
  ...NULL_LIKE_STRINGS,
  ...WHITESPACE_TRAPS,
  ...REST,
];

const INT_POOL = [
  '0',
  '-0',
  '1',
  '-1',
  '9007199254740991',
  '-9007199254740991',
  '2147483647',
  '-2147483648',
  '999999999999999',
  ' 42',
  '007',
  '+7',
  '1e3',
];

const FLOAT_POOL = [
  '0',
  '-0',
  '0.30000000000000004', // 0.1 + 0.2
  '-999999.99',
  '3.141592653589793',
  '1e21',
  '5e-324',
  '1.7976931348623157e308',
  '0.1',
  '1,234.56',
  '$1,299.99',
  'NaN',
  '-0.0',
  '1e-7',
];

const BOOLEAN_POOL = [
  'true', 'TRUE', 'True', 'false', 'FALSE', 't', 'f', 'yes', 'no', 'Y', 'N',
  '1', '0', 'null', '',
];

const DATE_POOL = [
  '2024-02-29',               // valid leap day
  '2023-02-29',               // invalid leap day
  '1900-02-29',               // century non-leap
  '1970-01-01T00:00:00Z',     // epoch zero
  '2038-01-19T03:14:07Z',     // 32-bit signed overflow
  '1969-12-31T23:59:59Z',     // one second before epoch
  '0001-01-01',
  '9999-12-31T23:59:59Z',
  '2024-03-10T02:30:00',      // non-existent US DST local time
  '2016-12-31T23:59:60Z',     // leap second
  '2024-02-30',               // invalid day-of-month
  '1700000000',               // epoch seconds
  '03/15/2024',               // US format
  '15.03.2024',               // EU format
];

const EMAIL_POOL = [
  '"quoted local"@example.com',
  'a'.repeat(64) + '@example.com',
  'ich@example.com',           // "üser" – IDN-ish local part
  'test@localhost',
  'x@example',
  'a@b.co',
  'TEST@EXAMPLE.COM',
  'user+tag+plus@example.com',
  ' trailing@example.com',
];

const UUID_POOL = [
  '00000000-0000-0000-0000-000000000000',
  'ffffffff-ffff-ffff-ffff-ffffffffffff',
  '550E8400-E29B-41D4-A716-446655440000',
  '550e8400-e29b-11d9-a716-446655440000',
  '{550e8400-e29b-41d4-a716-446655440000}',
  'urn:uuid:550e8400-e29b-41d4-a716-446655440000',
  'not-a-uuid',
  '550e8400e29b41d4a716446655440000',
];

const URL_POOL = [
  'http://',
  'https://example.com/path?q=1&r=<script>alert(1)</script>',
  'javascript:alert(1)',
  'https://例え.jp/パス',
  '//cdn.example.com/protocol-relative',
  'https://example.com/' + 'a'.repeat(200),
  'HTTPS://EXAMPLE.COM',
  'https://user:pass@example.com:8080/p?a=1#frag',
];

const POOLS: Record<string, string[]> = {
  string: STRING_POOL,
  int: INT_POOL,
  float: FLOAT_POOL,
  boolean: BOOLEAN_POOL,
  'iso-date': DATE_POOL,
  email: EMAIL_POOL,
  uuid: UUID_POOL,
  url: URL_POOL,
  unknown: STRING_POOL,
};

/** Deterministic 32-bit PRNG: same seed → same dataset, forever. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Max-length / extreme-value row: the classic buffer & overflow boundary. */
function boundaryValue(column: ColumnSchema): string {
  switch (column.type) {
    case 'int':
      return '9007199254740991';
    case 'float':
      return '1.7976931348623157e308';
    case 'boolean':
      return 'true';
    case 'iso-date':
      return '9999-12-31T23:59:59Z';
    case 'email':
      return 'a'.repeat(64) + '@example.com';
    case 'uuid':
      return 'ffffffff-ffff-ffff-ffff-ffffffffffff';
    case 'url':
      return 'https://example.com/' + 'a'.repeat(200);
    default:
      return 'a'.repeat(255);
  }
}

/** The first eight rows are fixed "themed" rows; the rest are seeded-random. */
const ROW_THEMES = [
  'nulls',        // every cell null
  'null-strings', // every cell "null"/"NaN"/"undefined"
  'empties',      // every cell an empty string
  'boundaries',   // max safe integer, 255 chars, year 9999
  'injection',    // SQLi + precision traps + leap day
  'xss-unicode',  // XSS + emoji/ZWJ/RTL
  'dates',        // epoch zero, 2038 overflow, invalid dates
  'numbers',      // -0, 0.1 + 0.2, currency strings
] as const;

type RowTheme = (typeof ROW_THEMES)[number];

function themedValue(theme: RowTheme, column: ColumnSchema, rand: () => number): string | null {
  const pick = <T>(items: T[]): T => items[Math.floor(rand() * items.length)];

  if (theme === 'nulls') return null;
  if (theme === 'null-strings') return pick(NULL_LIKE_STRINGS);
  if (theme === 'empties') return '';

  if (theme === 'boundaries') return boundaryValue(column);

  if (theme === 'injection') {
    if (column.type === 'string' || column.type === 'unknown') return pick(SQL_INJECTION);
    if (column.type === 'int' || column.type === 'float') return '9007199254740991';
    if (column.type === 'iso-date') return '2024-02-29';
    if (column.type === 'boolean') return 'true';
    return pick(SQL_INJECTION);
  }

  if (theme === 'xss-unicode') {
    if (column.type === 'string' || column.type === 'unknown') return pick(XSS);
    if (column.type === 'iso-date') return '1970-01-01T00:00:00Z';
    if (column.type === 'int' || column.type === 'float') return '0.30000000000000004';
    if (column.type === 'boolean') return 'false';
    return pick(XSS);
  }

  if (theme === 'dates') {
    if (column.type === 'iso-date') return pick(DATE_POOL);
    if (column.type === 'string' || column.type === 'unknown') return pick(UNICODE_TORTURE);
    if (column.type === 'int' || column.type === 'float') return '1700000000';
    if (column.type === 'boolean') return pick(BOOLEAN_POOL);
    return pick(DATE_POOL);
  }

  // 'numbers'
  if (column.type === 'int') return pick(INT_POOL);
  if (column.type === 'float') return pick(FLOAT_POOL);
  if (column.type === 'boolean') return pick(BOOLEAN_POOL);
  if (column.type === 'iso-date') return pick(DATE_POOL);
  if (column.type === 'string' || column.type === 'unknown') return '1,234.56';
  return pick(FLOAT_POOL);
}

export interface ChaosOptions {
  rows: number;
  seed: number;
}

export const DEFAULT_CHAOS_ROWS = 50;
export const MAX_CHAOS_ROWS = 5000;

/**
 * Synthesizes an adversarial-but-plausible dataset for the given schema.
 * `column.name` is used as the object key so exports line up with the CSV
 * headers the developer already knows.
 */
export function generateChaos(
  columns: ColumnSchema[],
  options: ChaosOptions,
): ChaosRow[] {
  const total = Math.max(1, Math.min(Math.floor(options.rows ?? DEFAULT_CHAOS_ROWS), MAX_CHAOS_ROWS));
  const seed = Number.isFinite(options.seed) ? Math.floor(options.seed) : 1;
  const rand = mulberry32(seed || 1);
  const pick = <T>(items: T[]): T => items[Math.floor(rand() * items.length)];

  const rows: ChaosRow[] = [];
  for (let index = 0; index < total; index++) {
    const row: ChaosRow = {};
    for (const column of columns) {
      if (index < ROW_THEMES.length) {
        row[column.name] = themedValue(ROW_THEMES[index], column, rand);
        continue;
      }
      // Mostly schema-plausible, with a minority of outright junk and nulls.
      const roll = rand();
      if (roll < 0.06) row[column.name] = null;
      else if (roll < 0.12) row[column.name] = pick(NULL_LIKE_STRINGS);
      else row[column.name] = pick(POOLS[column.type] ?? STRING_POOL);
    }
    // The null-strings theme row always carries a literal "null" so the
    // null-like family is visibly represented for every seed.
    if (index === 1 && columns.length > 0) row[columns[0].name] = 'null';
    rows.push(row);
  }
  ensureCoverage(columns, rows, rand);
  return rows;
}

/**
 * Deterministic coverage pass: guarantees every classic payload family is
 * represented at least once so torture-test exports are never toothless
 * for an unlucky seed. Only fills gaps — never removes variety.
 */
function ensureCoverage(
  columns: ColumnSchema[],
  rows: ChaosRow[],
  rand: () => number,
): void {
  if (rows.length === 0 || columns.length === 0) return;
  const flat = (): (string | null)[] => rows.flatMap((r) => columns.map((c) => r[c.name] ?? null));
  const has = (pred: (s: string) => boolean): boolean => flat().some((v) => v !== null && pred(v));
  const plant = (value: string, types: string[]): void => {
    if (has((s) => s.includes(value))) return;
    const eligible = columns.filter((c) => types.includes(c.type));
    const target = eligible.length > 0 ? eligible[Math.floor(rand() * eligible.length)] : columns[Math.floor(rand() * columns.length)];
    const row = rows[Math.floor(rand() * rows.length)];
    row[target.name] = value;
  };

  plant("' OR 1=1 --", ['string', 'unknown']);
  plant('<script>alert(1)</script>', ['string', 'unknown']);
  if (!has((s) => s.includes('👨‍👩‍👧‍👦'))) {
    // ZWJ family sequence (keep the exact PROMPT.md payload).
    plant('👨‍👩‍👧‍👦', ['string', 'unknown']);
  }
  if (!has((s) => /[؀-ۿ]{3,}/.test(s))) plant('مرحبا بالعالم', ['string', 'unknown']);
  if (!has((s) => /[֐-׿]{3,}/.test(s))) plant('אבג דהו וזחט', ['string', 'unknown']);
  if (!has((s) => s.length === 255)) plant('a'.repeat(255), ['string', 'unknown']);
  plant('0.30000000000000004', ['int', 'float']);
  plant('-999999.99', ['float', 'int']);
  plant('9007199254740991', ['int', 'float']);
  plant('2024-02-29', ['iso-date']);
  plant('1970-01-01T00:00:00Z', ['iso-date']);
  plant('2038-01-19T03:14:07Z', ['iso-date']);
  plant('2023-02-29', ['iso-date']);
}
